<template>
  <transition name="trail-card-slide">
    <div class="trail-data-card" :class="{ reduce: isReduced }" v-if="hasData">

      <div v-if="clusterNavActive" class="cluster-nav">
        <button class="cluster-nav-btn" @click="navigateClusterPrev">
          <span class="material-icons">chevron_left</span>
        </button>
        <span class="cluster-nav-label">{{ clusterCurrentIndex + 1 }} of {{ clusterTotalCount }}</span>
        <button class="cluster-nav-btn" @click="navigateClusterNext">
          <span class="material-icons">chevron_right</span>
        </button>
      </div>

      <TrailDataCardWeather
        v-if="showWeather"
        :showWeather="showWeather"
        :weather="weather?.start"
        :shortAddress="short_address"
        @hide-weather="hideWeather"
      />

      <div class="card-controls">
        <span class="close-button material-icons" @click="closeCard" title="Close card">
          close
        </span>
        <span class="hide-show material-icons" @click="toggleReduce" :title="isReduced ? 'Show details' : 'Hide details'">
          {{ isReduced ? 'add' : 'remove' }}
        </span>
      </div>
      <div class="left-panel align-content-center">
        <a v-if="shareableUrl" :href="shareableUrl" class="d-flex align-self-start thumbnail-container h-100">
          <img class="object-fit-cover" :src="mainImageUrl" alt="" />
        </a>

        <div class="px-2 w-100 d-flex flex-column justify-content-center p-2">
          <p class="d-flex flex-row flex-wrap mb-2">
            <button
              v-if="weather && weather.start"
              @click.stop="toggleWeather()"
              class="badge badge-dark d-flex align-items-center m-0 mr-1" style="cursor: pointer;"
              ref="weatherTooltipButton"
              type="button"
            >
              <span
                ref="weatherTooltip"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                data-bs-html="true"
                :title="weatherTooltipContent"
                style="pointer-events: auto;"
                class="d-flex align-items-center"
              >
                <small class="pr-0">
                  <img :src="`https://openweathermap.org/img/wn/${weather?.start?.current?.icon}@2x.png`"
                                         width="20" height="20" alt="" />
                </small>
                {{ Math.round(weather?.start?.daily?.temperature_2m_max?.[0]) }}°C
              </span>
              <span class="material-icons ml-1" style="font-size: 1.2em;"><strong>add</strong></span>
            </button>

            <span
              v-if="averageRating"
              class="badge badge-dark d-flex align-items-center m-0 mr-1" >
              <small>
                <span class="material-icons mr-1">star</span>
              </small>
              {{ averageRating }}
            </span>

            <span class="dropdown">
              <span
                class="badge badge-dark d-flex align-items-center m-0 mr-1" type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style="height: 100% !important; cursor: pointer;"
              >
                <span
                  ref="optionsButtonTooltip"
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  data-bs-html="true"
                  title="See options to modify this trail."
                >
                  <small>
                    <span class="material-icons" style="font-size: 17px;">more_vert</span>
                  </small>
                </span>
              </span>
              <ul class="dropdown-menu">
                <li class="dropdown-item" @click="onGpxClick">
                  Export GPX
                </li>
                <li v-if="showEditInPlanner" class="dropdown-item" @click="editInPlanner">
                  {{ editInPlannerText }}
                </li>
                <li v-if="isDetailsEditable" class="dropdown-item" @click="editDetails">
                  Edit Details
                </li>
                <li class="dropdown-item" @click="onShareClick">
                  Share
                </li>
                <li class="dropdown-item" @click="preview">
                  3D Flyover
                </li>
                <li v-if="isCropable" class="dropdown-item" @click="crop">
                  Crop recording
                </li>
                <li v-if="isGarminConnected()" class="dropdown-item" @click="sendToGarmin">
                  Send to Garmin
                </li>
                <li v-if="canReportRecordingIssue" class="dropdown-item" @click="reportRecordingIssue">
                  Report as inaccurate
                </li>
                <li v-if="isDeletable" class="dropdown-item" @click="onDeleteClick">
                  Delete
                </li>
              </ul>
            </span>

            <a v-if="shareableUrl" :href="shareableUrl" class="badge badge-dark d-flex align-items-center m-0 mr-1" title="Open trail page"> <small><span class="material-icons" style="font-size: 17px;">open_in_new</span></small>
            </a>
          </p>

          <a :href="shareableUrl || '#'" :target="shareableUrl ? '_blank' : '_self'"><strong>{{ name }}</strong></a> <p v-if="short_address" class="mb-1">{{ short_address }}</p>
          <p v-if="difficulty" class="text-muted"><small>{{ difficulty }}</small></p> <p>
          <small>
            {{ formatDistance(distanceMeters) }}
            <span class="mx-1">&#183;</span>
            {{ formatElevation(elevationGainMeters) }}
            <span class="mx-1">&#183;</span>
            ~{{ duration() }}
          </small>
        </p>
        </div>
      </div>

      <div class="right-panel p-2 position-relative">
        <div class="elevation-profile-placeholder">
          <div class="chart-container">
            <canvas ref="chartCanvas"></canvas>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { defineComponent, nextTick, markRaw } from 'vue';
import mapboxgl from 'mapbox-gl';
// ----- CHART.JS v3/v4 IMPORT -----
import { Chart, registerables } from 'chart.js';
import { distance as turfDistance } from '@turf/turf';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref';
import { HikeTimeUtility, PrettyTime } from '../../utils/helpers/HikeTime';
import getWeatherIcon from '../../utils/getWeatherIcon';
import getWeatherDescription from "../../utils/getWeatherDescription";
import TrailDataCardWeather from './TrailDataCardWeather.vue';
import { geojsonToGpx } from "../../utils/geojsonToGpx";
import graph_client from "../../utils/graphql/graph_client";
import { DestroyPtv2PlannedTrail } from "../../utils/graphql/planned-trail-graph";
import { RevenueCatWrapper } from "../../utils/RevenueCatWrapper";
import { HiikerMapStyle } from "../../utils/models/MapStyles";
import { DestroyRecording } from "../../utils/graphql/recorded-trail-graph";
import { Tooltip } from 'bootstrap';
import {SEND_TO_GARMIN} from "../../utils/graphql/send-to-garmin";
import { timestampToIso } from "../../utils/timestampToIso";
import {getFeatureCollectionLineString} from "../../utils/getFeatureCollectionLineString";

// ----- CHART.JS v3/v4 REGISTRATION -----
Chart.register(...registerables);

// Keep a global reference to the marker
let elevationMarker: mapboxgl.Marker | null = null;

export default defineComponent({
  name: 'TrailDataCard',
  props: {
    reduced: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    TrailDataCardWeather,
  },
  data() {
    return {
      // UI
      isReduced: this.reduced,
      isLoading: false,
      isDetailsEditable: false,
      actionMenuOpen: false,

      // Trail info
      id: '',
      obfuscatedSlug: '', // Store obfuscatedSlug for recordings and hikes
      ownerFirebaseUserId: null as string | null, // Store owner's firebase user ID
      type: '',
      name: '',
      short_address: '',
      difficulty: '',
      averageRating: '',
      elevationGainMeters: 0,
      distanceMeters: 0,
      durationSeconds: 0,
      isDeletable: false,
      isCropable: false,

      // Geometry & images
      featureCollection: null as any,
      mainImageUrl: '',
      createdAt: '',
      shareableUrl: '',
      startPoint: null as number[] | null,
      endPoint: null as number[] | null,

      timestamps: null as string[] | undefined,

      // Chart
      chartInstance: null as any,
      chartLat: [] as number[],
      chartLon: [] as number[],

      // Cluster navigation
      clusterNavActive: false,
      clusterCurrentIndex: 0,
      clusterTotalCount: 0,

      // Weather
      weather: null as any,
      showWeather: false,
      weatherTooltip: null as Tooltip | null, // Typed Bootstrap Tooltip
      optionsTooltip: null as Tooltip | null, // Typed Bootstrap Tooltip
      editUrl: null as string | null, // Typed editUrl
    };
  },
  watch: {
    reduced(newVal) {
      this.isReduced = newVal;
    },
    // --- WATCHER TO REDRAW CHART ON DATA CHANGE ---
    featureCollection: {
      // immediate: true, // Removed immediate, rely on mounted and manual calls
      deep: true, // Add deep watch if featureCollection structure might change internally
      handler() {
        // Ensure chart redraws when data is loaded/changed after mount
        this.redrawChartSafely();
        this.setStartPointAndEndPointFromFeatureCollection(); // Recalculate points if data changes
        this.getWeather(); // Fetch new weather if points change
      },
    },
  },
  computed: {
    hasData() {
      return !!this.featureCollection && !!this.featureCollection.features?.length;
    },
    currentWeatherDescription() {
      if (!this.weather?.start?.daily) return '';
      return getWeatherDescription(this.weather.start.daily.weather_code[0]);
    },
    weatherTooltipContent() {
      return `<span class="text-center d-flex flex-column"><strong>${this.currentWeatherDescription}</strong><small style='opacity: 0.5;'>click for more</small></span>`;
    },
    showEditInPlanner() {
      // Always show edit option for hikes on explore map
      return this.type === 'Hike';
    },
    editInPlannerText() {
      return 'Edit a Copy';
    },
    canReportRecordingIssue() {
      return this.type === 'Recording' && this.isCurrentUserAdmin();
    },

    simplifiedChartData() {
      if (!this.featureCollection?.features) return null;

      const coords = this.getFeatureCollectionLineString();
      console.log('Coordinates for chart:', coords);
      if (!coords || !coords.length) return null;

      const maxPoints = 50;
      const step = Math.max(1, Math.floor(coords.length / maxPoints)); // Ensure step >= 1

      const measurementUtils = MeasurementPrefUtility.preference();
      const xLabels: string[] = [];
      // {x, y} points keyed on real cumulative distance so the linear x-axis
      // spaces points by distance, not array index (no "stepping" on sparse
      // straight segments).
      const dataPoints: { x: number; y: number }[] = [];
      const latArray: number[] = [];
      const lonArray: number[] = [];

      let distanceAccum = 0;
      // Ensure the loop runs correctly and includes the last point if necessary
      for (let i = 0; i < coords.length; i += step) {
        const currentCoord = coords[i];
        const prevCoord = i === 0 ? currentCoord : coords[i - step]; // Use currentCoord if i=0

        if (i > 0) {
          // Calculate distance segmentally for accuracy
          let segmentDistance = 0;
          for(let j = i - step + 1; j <= i; j++) {
            if(j < coords.length) { // Check bounds
              segmentDistance += turfDistance(coords[j-1], coords[j], { units: 'meters' });
            }
          }
          distanceAccum += segmentDistance;
        }

        const [lon, lat, elevation] = currentCoord;
        const ele = elevation || 0;

        xLabels.push(measurementUtils.bigUnitFormatter(distanceAccum / 1000, false));
        dataPoints.push({ x: measurementUtils.bigUnitValue(distanceAccum / 1000), y: measurementUtils.smallUnitValue(ele) });
        latArray.push(lat);
        lonArray.push(lon);
      }

      // Optionally, ensure the very last point is included if step doesn't land on it
      if ((coords.length -1) % step !== 0 && coords.length > 1) {
        const lastCoord = coords[coords.length - 1];
        let segmentDistance = 0;
        const lastIncludedIndex = Math.floor((coords.length - 1) / step) * step;
        for(let j = lastIncludedIndex + 1; j < coords.length; j++) {
          segmentDistance += turfDistance(coords[j-1], coords[j], { units: 'meters' });
        }
        distanceAccum += segmentDistance;

        const [lon, lat, elevation] = lastCoord;
        xLabels.push(measurementUtils.bigUnitFormatter(distanceAccum / 1000, false));
        dataPoints.push({ x: measurementUtils.bigUnitValue(distanceAccum / 1000), y: measurementUtils.smallUnitValue(elevation || 0) });
        latArray.push(lat);
        lonArray.push(lon);
      }


      return {
        labels: xLabels,
        datasets: [
          {
            label: 'Elevation',
            data: dataPoints,
            borderColor: '#ffffff',
            borderWidth: 2,
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.1,
            fill: true,
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
          },
        ],
        // Store lat/lon directly on the root for easier access in hover
        lat: latArray,
        lon: lonArray,
      };
    },
  },
  methods: {
    isGarminConnected() {
      if (window.user) {
        return window.user.garmin_connected || false;
      } else {
        return false
      }
    },
    getCurrentUserFirebaseId() {
      const user = (window as any).user;
      return user?.firebaseUserId || user?.firebase_user_id || null;
    },
    isOwnedByCurrentUser(ownerFirebaseUserId: string | null) {
      const currentUserFirebaseId = this.getCurrentUserFirebaseId();
      if (!ownerFirebaseUserId || !currentUserFirebaseId) {
        return false;
      }
      return ownerFirebaseUserId === currentUserFirebaseId;
    },
    isCurrentUserAdmin() {
      const user = (window as any).user;
      if (user?.admin === true || user?.admin === 'true') {
        return true;
      }

      const adminFlag = (window as any).admin;
      return adminFlag === true || adminFlag === 'true';
    },
    getRecordingReportIssueUrl() {
      if (this.obfuscatedSlug) {
        return `/recordings/users/${this.obfuscatedSlug}/report_issue`;
      }

      if (this.shareableUrl) {
        try {
          let shareablePath = new URL(this.shareableUrl, window.location.origin).pathname.replace(/\/$/, '');
          if (shareablePath.startsWith('/maps/recordings/')) {
            shareablePath = shareablePath.replace('/maps/recordings/', '/recordings/users/');
          }
          return `${shareablePath}/report_issue`;
        } catch (error) {
          console.error('Failed to parse recording shareable URL:', error);
        }
      }

      if (window.location.pathname.match(/^\/recordings\/users\/[^/]+(\/map)?$/)) {
        return window.location.pathname.replace(/\/map$/, '').replace(/\/$/, '') + '/report_issue';
      }

      return null;
    },
    navigateClusterPrev() {
      (window as any).clusterNavigate?.('prev');
    },
    navigateClusterNext() {
      (window as any).clusterNavigate?.('next');
    },
    closeCard() {
      (window as any).clearClusterNav?.();
      window.trailCardClearData?.();
      window.removeTrailLine?.();
    },
    toggleReduce() {
      this.isReduced = !this.isReduced;
      this.$emit('update:reduced', this.isReduced);
      this.$emit('toggle-reduce', this.isReduced);
    },
    hideWeather() {
      this.showWeather = false;
    },
    addTooltip() {
      // Dispose existing tooltips to prevent memory leaks
      if (this.weatherTooltip) {
        this.weatherTooltip.dispose();
        this.weatherTooltip = null;
      }
      if (this.optionsTooltip) {
        this.optionsTooltip.dispose();
        this.optionsTooltip = null;
      }

      // Don't initialize tooltips on small screens (mobile)
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        return; // Skip tooltip initialization on mobile
      }

      // Use nextTick to ensure refs are available after DOM updates
      this.$nextTick(() => {
        const weatherEl = this.$refs.weatherTooltip as Element | undefined;
        if (weatherEl) {
          this.weatherTooltip = new Tooltip(weatherEl, {
            trigger: "hover",
            placement: "top",
            customClass: 'weather-tooltip',
            html: true // Ensure HTML content is allowed if needed
          });
        }

        const optionsEl = this.$refs.optionsButtonTooltip as Element | undefined;
        if (optionsEl) {
          this.optionsTooltip = new Tooltip(optionsEl, {
            trigger: "hover",
            placement: "top",
            customClass: 'weather-tooltip', // Consider a different class if styling differs
            html: true // Ensure HTML content is allowed
          });
        }
      });
    },
    toggleWeather() {
      this.showWeather = !this.showWeather;
      console.log('Toggling weather:', this.showWeather);
    },
    onGpxClick() {
      const coords = this.getFeatureCollectionLineString();
      if (!coords) return;

      const gpx = geojsonToGpx({
        type: "Feature",
        properties: {}, // Add relevant properties if needed
        geometry: {
          type: "LineString",
          coordinates: coords,
        }
      }, this.timestamps,{
        creator: 'Hiiker', // Set creator metadata
        metadata: {
          name: this.name, // Add trail name to GPX metadata
          time: this.createdAt // Add creation time if available
        }
      });

      const blob = new Blob([gpx], { type: 'application/gpx+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.name || 'track'}.gpx`; // Use trail name for filename
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    onShareClick() {
      if(navigator.clipboard && this.shareableUrl) {
        navigator.clipboard.writeText(this.shareableUrl).then(() => {
          alert('Hike share URL copied to clipboard');
        }).catch(err => {
          console.error('Failed to copy URL: ', err);
          alert('Could not copy URL. Please try again.');
        });
      } else {
        alert('Clipboard API not available.');
      }
    },
    preview() {
      // Assuming these global functions exist and handle errors
      try {
        if (this.type === 'Recording' && window.showMapModalForRecordedTrail) {
          window.showMapModalForRecordedTrail(this.id);
        } else if (this.type === 'Ptv2::PlannedTrail' && window.showMapModalForPlannedTrail) {
          window.showMapModalForPlannedTrail(this.id);
        } else if ((this.type === 'Trail' || this.type === 'Hike') && window.showMapModalForTrail) {
          // showMapModalForTrail handles both Trail and Hike types (loads Hike data)
          window.showMapModalForTrail(this.id);
        } else {
          console.warn('Preview function not found for type:', this.type);
        }
      } catch (error) {
        console.error("Error calling preview function:", error);
        alert("Could not open preview.");
      }
    },
    crop() {
      if(this.shareableUrl) {
        window.location.href = `${this.shareableUrl}/crop`;
      }
    },
    sendToGarmin() {
      graph_client.request(SEND_TO_GARMIN, {klass: this.type, id: this.id}).then((response) => {
        if (response.sendToGarmin) {
          alert('Hike sent to Garmin successfully!');
        } else {
          alert('Failed to send trail to Garmin. Please try again later.');
        }
      }).catch((error) => {
        console.error('Error sending trail to Garmin:', error);
        alert('An error occurred while sending the trail to Garmin. Please check the console for details.');
      });
    },
    async reportRecordingIssue() {
      if (!this.canReportRecordingIssue) {
        return;
      }

      const reportUrl = this.getRecordingReportIssueUrl();
      if (!reportUrl) {
        window.alert('Unable to mark this recording as inaccurate right now.');
        return;
      }

      try {
        const response = await fetch(reportUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content || '',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        const data = await response.json().catch(() => ({}));
        if (response.ok && data.success) {
          window.alert('Recording marked as inaccurate.');
        } else {
          window.alert(data.message || 'Unable to mark this recording as inaccurate at this time. Please try again later.');
        }
      } catch (error) {
        console.error('Error reporting recording issue:', error);
        window.alert('Unable to mark this recording as inaccurate at this time. Please try again later.');
      }
    },
    editInPlanner() {
      // Check if user is logged in using window.user
      if (!(window as any).user) {
        // User not logged in - trigger login
        if (window.showSignInModal) {
          window.showSignInModal();
        } else {
          alert('Please sign in to edit this hike');
        }
        return;
      }

      // Navigate to planner with from_hike_id parameter (matches hike show page behavior)
      if (this.type === 'Hike' && this.obfuscatedSlug) {
        window.location.href = `/hike-planner?from_hike_id=${this.obfuscatedSlug}`;
      }
    },
    editDetails() {
      if(window.showEditRecordingModal) {
        try {
          // Use obfuscatedSlug instead of numeric ID for the edit modal
          const slugToUse = this.obfuscatedSlug || this.id;
          console.log('TrailDataCard editDetails - using slug:', slugToUse);
          window.showEditRecordingModal(slugToUse);
        } catch (error) {
          console.error("Error calling edit details modal:", error);
          alert("Could not open edit details.");
        }
      } else {
        console.warn('showEditRecordingModal function not found');
      }
    },
    async onDeleteClick() { // Make async for potential await later
      const confirmClear = confirm('Are you sure you want to delete this route? This action cannot be undone.');
      if (confirmClear) {
        this.isLoading = true; // Optional: Show loading state
        try {
          let deletePromise;
          if (this.type === 'Recording') {
            deletePromise = graph_client.request(DestroyRecording, { input: { id: this.id } });
          } else if (this.type === 'Ptv2::PlannedTrail') {
            deletePromise = graph_client.request(DestroyPtv2PlannedTrail, { plannedTrail: { id: this.id } });
          } else if (this.type === 'Trail') {
            console.warn('Deletion for type "Trail" is not implemented yet.');
            alert('Deletion for this trail type is not yet supported.');
            this.isLoading = false;
            return; // Exit if type not supported
          } else {
            console.warn('Unknown type for deletion:', this.type);
            alert('Cannot delete unknown trail type.');
            this.isLoading = false;
            return; // Exit if type not supported
          }

          await deletePromise; // Wait for the GraphQL request
          alert(`${this.type} deleted successfully. Page will now reload.`);
          window.location.reload();

        } catch (err) {
          console.error(`Failed to delete ${this.type}:`, err);
          alert(`Failed to delete ${this.type}. Please check the console and try again later.`);
          this.isLoading = false; // Ensure loading state is turned off on error
        }
      }
    },
    getFeatureCollectionLineString() {
      return getFeatureCollectionLineString(this.featureCollection);
    },
    createElevationMarker() {
      const el = document.createElement('div');
      el.style.display = 'block';
      el.style.border = '3px solid #fff';
      el.style.cursor = 'pointer';
      el.style.padding = '0px';
      el.style.backgroundColor = '#e74c3c';
      el.style.borderRadius = '50%';
      el.style.width = '20px';
      el.style.height = '20px';
      el.style.boxShadow = '0 0 5px rgba(0,0,0,0.5)';
      return new mapboxgl.Marker({ element: el });
    },
    removeElevationMarker() {
      if (elevationMarker) {
        elevationMarker.remove();
        elevationMarker = null;
      }
    },
    formatDistance(distance: number) {
      const measurementPref = MeasurementPrefUtility.preference();
      return measurementPref.bigUnitFormatter(distance / 1000, true); // Use bigUnitFormatter for distance
    },
    formatElevation(elevation: number) {
      const measurementPref = MeasurementPrefUtility.preference();
      return measurementPref.smallUnitFormatter(elevation, false);
    },
    duration() {
      const prettyTime = new PrettyTime(this.durationSeconds).prettyTime();
      return prettyTime;
    },
    setStartPointAndEndPointFromFeatureCollection() {
      const coords = this.getFeatureCollectionLineString();
      if (coords && coords.length > 0) {
        this.startPoint = coords[0];
        this.endPoint = coords[coords.length - 1];
      } else {
        this.startPoint = null;
        this.endPoint = null;
      }
    },
    // ----------------------------------------------
//  put these two functions in <script lang="ts">
// ----------------------------------------------
    /* -------------------------------------------
 *  100 % drop-in for your existing component
 * -------------------------------------------*/
    async getWeather() {
      this.setStartPointAndEndPointFromFeatureCollection();
      if (!this.startPoint) { this.weather = null; this.addTooltip(); return; }

      const [lng, lat] = this.startPoint;
      const key        = import.meta.env.VITE_OPENWEATHER_KEY;

      const url = new URL('https://api.openweathermap.org/data/2.5/weather');
      url.search = new URLSearchParams({
        lat : lat.toString(),
        lon : lng.toString(),
        units : 'metric',
        appid : key,
      }).toString();

      try {
        const r = await fetch(url.toString());
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();                    //  ← the object you pasted
        this.weather = { start: this.fromOWM(json) };   //  ← adapter keeps UI intact
      } catch (e) {
        console.error('OWM fetch error', e);
        this.weather = null;
      } finally { this.addTooltip(); }
    },

    /**
     * Convert the OpenWeatherMap One-Call object so the rest of
     * the component does **not** need to change.
     */
    /**
     * Convert the `/weather` payload so the rest of
     * the code can stay exactly as it is.
     */
    fromOWM(w: any) {
      const day   = w.dt >= w.sys.sunrise && w.dt < w.sys.sunset ? 1 : 0;
      const rain  = w.rain?.['1h'] ?? 0;                         // may be undefined

      return {
        lat : w.coord.lat,
        lng : w.coord.lon,
        elevation : null,

        /* ------------ current ------------- */
        current : {
          temperature_2m : w.main.temp,
          weather_code   : w.weather?.[0]?.id,   //  numeric id (e.g. 800)
          icon           : w.weather?.[0]?.icon, //  "01d", "10n", …
          is_day         : day,
        },

        /* ------------ daily (array) ------------- */
        daily : {
          temperature_2m_max : [w.main.temp_max],
          temperature_2m_min : [w.main.temp_min],
          weather_code       : [w.weather?.[0]?.id],
          precipitation_sum  : [rain],
          sunrise            : [w.sys.sunrise * 1000],
          sunset             : [w.sys.sunset * 1000],
        },
      };
    },

    // --- WRAPPER TO SAFELY REDRAW CHART ---
    redrawChartSafely() {
      // Use nextTick to ensure the DOM (canvas ref) is ready, especially after v-if changes
      nextTick(() => {
        if (this.$refs.chartCanvas && this.hasData) {
          this.drawChart();
        } else if (this.chartInstance) {
          // If no data or canvas, destroy existing chart
          this.chartInstance.destroy();
          this.chartInstance = null;
        }
      });
    },

    // ----- CHART.JS v3/v4 DRAW FUNCTION -----
    drawChart() {
      const chartData = this.simplifiedChartData;
      if (!chartData) {
        if (this.chartInstance) {
          this.chartInstance.destroy();
          this.chartInstance = null;
        }
        this.chartLat = [];
        this.chartLon = [];
        return;
      }

      // Store lat/lon arrays separately (Chart.js may strip custom data properties)
      this.chartLat = chartData.lat;
      this.chartLon = chartData.lon;

      const canvas = this.$refs.chartCanvas as HTMLCanvasElement;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Remove old canvas listeners and destroy old chart instance
      if (this.chartInstance) {
        canvas.removeEventListener('mousemove', this.handleChartMouseMove);
        canvas.removeEventListener('mouseleave', this.handleChartMouseLeave);
        this.chartInstance.destroy();
        this.chartInstance = null;
      }

      // Custom plugin: vertical hover line
      const verticalLinePlugin = {
        id: 'verticalHoverLine',
        afterDraw: (chart: Chart) => {
          const activeElements = chart.getActiveElements();
          if (activeElements && activeElements.length > 0) {
            const activePoint = activeElements[0];
            const chartCtx = chart.ctx;
            const x = activePoint.element.x;
            const topY = chart.chartArea.top;
            const bottomY = chart.chartArea.bottom;

            chartCtx.save();
            chartCtx.beginPath();
            chartCtx.moveTo(x, topY);
            chartCtx.lineTo(x, bottomY);
            chartCtx.lineWidth = 1;
            chartCtx.strokeStyle = '#ccc';
            chartCtx.stroke();
            chartCtx.restore();
          }
        },
      };

      // Pin the linear x-axis to the route's real end distance, otherwise
      // Chart.js rounds the max up and leaves empty profile at the tail.
      const points = (chartData as any).datasets?.[0]?.data;
      const lastX = points && points.length ? points[points.length - 1].x : undefined;
      const tooltipPref = MeasurementPrefUtility.preference();

      // Create the chart
      this.chartInstance = markRaw(new Chart(ctx, {
        type: 'line',
        data: chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            legend: { display: false },
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                title: (items: any[]) => `${items[0].parsed.x} ${tooltipPref.kilometerSymbol()}`,
              }
            }
          },
          scales: {
            x: {
              // Linear distance axis: points carry real cumulative distance as
              // {x, y}, so spacing reflects distance rather than array index.
              type: 'linear',
              min: 0,
              max: lastX,
              ticks: {
                color: '#ffffff',
                font: { size: 10 },
                callback: (v: number | string) => `${v} ${tooltipPref.kilometerSymbol()}`,
              },
              grid: {
                color: 'rgba(255, 255, 255, 0.1)'
              },
            },
            y: {
              beginAtZero: false,
              ticks: {
                color: '#ffffff',
                font: { size: 10 }
              },
              grid: {
                color: 'rgba(255, 255, 255, 0.1)'
              },
            },
          },
        },
        plugins: [verticalLinePlugin],
      }));

      // Attach native mouse event listeners for elevation marker
      canvas.addEventListener('mousemove', this.handleChartMouseMove);
      canvas.addEventListener('mouseleave', this.handleChartMouseLeave);
    },
    handleChartMouseMove(event: MouseEvent) {
      const chart = this.chartInstance;
      if (!chart) return;

      const elements = chart.getElementsAtEventForMode(
        event, 'index', { intersect: false }, false
      );

      if (!elements || elements.length === 0) {
        this.removeElevationMarker();
        return;
      }

      const hoveredIndex = elements[0].index;

      if (this.chartLat.length > 0 && hoveredIndex < this.chartLat.length) {
        const lat = this.chartLat[hoveredIndex];
        const lon = this.chartLon[hoveredIndex];

        if (!elevationMarker) {
          elevationMarker = this.createElevationMarker();
        }
        if ((window as any).map && typeof (window as any).map.getCenter === 'function') {
          try {
            elevationMarker.setLngLat([lon, lat]).addTo((window as any).map);
          } catch (e) {
            console.error("Error adding elevation marker to map:", e);
            this.removeElevationMarker();
          }
        }

        // Update active elements so the vertical line plugin draws
        chart.setActiveElements([{ datasetIndex: 0, index: hoveredIndex }]);
        chart.update('none');
      } else {
        this.removeElevationMarker();
      }
    },
    handleChartMouseLeave() {
      this.removeElevationMarker();
      if (this.chartInstance) {
        this.chartInstance.setActiveElements([]);
        this.chartInstance.update('none');
      }
    },

    // --- WINDOW FUNCTION SETUP ---
    setupWindowFunctions() {
      window.trailCardDataHike = (hike) => {
        console.log("Loading Hike Data:", hike.id);
        this.id = hike.id;
        this.obfuscatedSlug = hike.obfuscatedSlug || '';
        this.ownerFirebaseUserId = hike.user?.firebaseUserId || null;
        this.name = hike.name || 'Unnamed Hike';
        this.short_address = hike.startAddress?.cityCountry;
        this.featureCollection = JSON.parse(hike.geojsonRoutesOnlyString)
        this.elevationGainMeters = hike.elevationGainMeters || 0;
        this.durationSeconds = (hike.durationMinutes || 0) * 60; // Convert minutes to seconds
        this.mainImageUrl = hike.staticMapImageUrl || ''; // Provide fallback
        this.createdAt = hike.createdAt;
        this.shareableUrl = hike.shareableUrl;
        this.distanceMeters = hike.distanceMeters || 0;
        this.type = 'Hike';
        this.editUrl = null; // Always use click handler for hikes on explore map
        this.isDeletable = false; // Hikes usually not deletable by user this way
        this.isCropable = false;
        this.isDetailsEditable = false;
        this.difficulty = hike.difficulty || ''; // Use provided difficulty or empty string
        this.averageRating = hike.averageRating || ''; // Use provided rating or empty string
        this.$emit('card-active', true); // Emit active state
      }
      window.trailCardDataRecording = (recording) => {
        console.log("Loading Recording Data:", recording.id);
        this.id = recording.id;
        this.obfuscatedSlug = recording.obfuscatedSlug || ''; // Store obfuscatedSlug
        this.name = recording.title || 'Unnamed Recording';
        this.short_address = recording.startAddress?.city;
        this.featureCollection = typeof recording.lineString === 'string' ? JSON.parse(recording.lineString) : recording.lineString; // Handle parsed/unparsed
        this.elevationGainMeters = recording.elevationGainMeters || 0;
        this.durationSeconds = recording.elapsedSeconds || 0;
        this.mainImageUrl = recording.thumbnailUrl || ''; // Provide fallback
        this.createdAt = recording.createdAt;
        this.shareableUrl = recording.shareableUrl;
        this.distanceMeters = recording.distanceMeters || 0;
        this.type = 'Recording';
        this.ownerFirebaseUserId = recording.publicUser?.firebaseUserId
          || recording.publicUser?.firebase_user_id
          || recording.user?.firebaseUserId
          || recording.user?.firebase_user_id
          || null;
        this.editUrl = `/hike-planner?trail_type=Recording&trail_id=${recording.id}`;
        const isOwner = this.isOwnedByCurrentUser(this.ownerFirebaseUserId);
        this.isDeletable = isOwner;
        this.isCropable = isOwner;
        this.isDetailsEditable = isOwner;
        this.difficulty = ''; // Clear fields not applicable to Recording
        this.averageRating = '';
        this.$emit('card-active', true); // Emit active state
        this.timestamps = recording.timeStamps
        // this.redrawChartSafely(); // Redraw is handled by watcher now
      };

      window.trailCardDataPtv2PlannedTrail = (plannedTrail) => {
        console.log("Loading Planned Trail Data:", plannedTrail.id);
        this.id = plannedTrail.id;
        this.name = plannedTrail.title || 'Unnamed Plan';
        this.type = 'Ptv2::PlannedTrail';
        this.featureCollection = plannedTrail.geojsonFeatureCollection; // Assumed already object
        this.elevationGainMeters = plannedTrail.elevationGainMeters || 0;
        this.durationSeconds = (plannedTrail.calculatedDurationMinutes || 0) * 60;
        this.mainImageUrl = plannedTrail.thumbnailUrl || '';
        this.createdAt = plannedTrail.createdAt;
        this.shareableUrl = plannedTrail.shareableUrl;
        this.distanceMeters = plannedTrail.distanceMeters || 0;
        this.editUrl = this.shareableUrl ? `${this.shareableUrl}/edit` : '';
        this.isDeletable = true;
        this.isCropable = false;
        this.isDetailsEditable = false;
        this.difficulty = ''; // Clear fields not applicable
        this.averageRating = '';
        this.$emit('card-active', true);
        // this.redrawChartSafely(); // Redraw is handled by watcher now
      };

      (window as any).trailCardSetClusterNav = (active: boolean, index: number, total: number) => {
        this.clusterNavActive = active;
        this.clusterCurrentIndex = index;
        this.clusterTotalCount = total;
      };

      window.trailCardClearData = () => {
        console.log("Clearing Trail Card Data");
        this.id = '';
        this.name = '';
        this.type = '';
        this.featureCollection = null;
        this.weather = null;
        this.isCropable = false;
        this.isDeletable = false;
        this.isDetailsEditable = false;
        this.ownerFirebaseUserId = null;
        this.mainImageUrl = '';
        this.shareableUrl = '';
        this.editUrl = null;
        this.difficulty = '';
        this.averageRating = '';
        this.showWeather = false; // Hide weather panel
        this.clusterNavActive = false;
        this.clusterCurrentIndex = 0;
        this.clusterTotalCount = 0;
        if (this.chartInstance) { // Destroy chart
          this.chartInstance.destroy();
          this.chartInstance = null;
        }
        if (elevationMarker) { // Remove map marker
          elevationMarker.remove();
          elevationMarker = null;
        }
        this.$emit('card-active', false); // Card becomes inactive
      };

      window.trailCardDataTrail = (trail) => {
        console.log("Loading Trail Data:", trail.id);
        this.id = trail.id;
        this.name = trail.name || 'Unnamed Trail';
        this.difficulty = trail.difficulty;
        this.averageRating = trail.averageRating;
        this.type = 'Trail';
        this.featureCollection = typeof trail.lineString?.geoData === 'string' ? JSON.parse(trail.lineString.geoData) : trail.lineString?.geoData; // Handle parsed/unparsed
        this.durationSeconds = (trail.durationMinutes || 0) * 60;
        this.elevationGainMeters = trail.totalElevation || 0; // Use totalElevation field
        this.distanceMeters = trail.trailDistance || 0; // Use trailDistance field

        if (trail.photos && trail.photos.length > 0 && trail.photos[0].url) {
          this.mainImageUrl = trail.photos[0].url;
        } else {
          this.mainImageUrl = trail.mobileStaticMapImage || ''; // Fallback
        }
        this.createdAt = trail.createdAt;
        const origin = window.location.origin;
        // Ensure slugs exist before constructing URL
        this.shareableUrl = (trail.countrySlug && trail.regionSlug && trail.slug)
          ? `${origin}/trails/${trail.countrySlug}/${trail.regionSlug}/${trail.slug}`
          : '';
        this.editUrl = `/hike-planner?trail_type=Trail&trail_id=${trail.id}`;
        this.isDeletable = false; // Trails usually not deletable by user this way
        this.isCropable = false;
        this.isDetailsEditable = false;
        this.$emit('card-active', true); // Card becomes active
        // this.redrawChartSafely(); // Redraw is handled by watcher now
      };
    }
  },
  created() {
    // Expose window.* methods for loading data into this card
    this.setupWindowFunctions();
  },
  mounted() {
    // Initial draw attempt when component mounts, if data might already be present
    // The watcher will handle subsequent data loads/changes.
    this.redrawChartSafely();
    this.addTooltip(); // Initialize tooltips on mount
  },
  beforeUnmount() {
    // Clean up chart instance
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
    // Clean up canvas listeners and map marker
    const canvas = this.$refs.chartCanvas as HTMLCanvasElement | undefined;
    if (canvas) {
      canvas.removeEventListener('mousemove', this.handleChartMouseMove);
      canvas.removeEventListener('mouseleave', this.handleChartMouseLeave);
    }
    if (elevationMarker) {
      elevationMarker.remove();
      elevationMarker = null;
    }
    // Clean up tooltips
    if (this.weatherTooltip) {
      this.weatherTooltip.dispose();
    }
    if (this.optionsTooltip) {
      this.optionsTooltip.dispose();
    }
    // Optional: Clean up global functions if appropriate for your app structure
    // delete window.trailCardDataRecording;
    // delete window.trailCardDataPtv2PlannedTrail;
    // delete window.trailCardClearData;
    // delete window.trailCardDataTrail;
  },
});

// ----- DECLARE GLOBAL WINDOW FUNCTIONS (TypeScript) -----
// This tells TypeScript that these functions might exist on the window object
declare global {
  interface Window {
    trailCardDataRecording?: (recording: any) => void;
    trailCardDataHike?: (hike: any) => void;
    trailCardDataPtv2PlannedTrail?: (plannedTrail: any) => void;
    trailCardClearData?: () => void;
    trailCardSetClusterNav?: (active: boolean, index: number, total: number) => void;
    trailCardDataTrail?: (trail: any) => void;
    clusterNavigate?: (direction: 'next' | 'prev') => void;
    clearClusterNav?: () => void;
    user?: any;
    admin?: boolean | string;
    showMapModalForRecordedTrail?: (id: string, shareConfig?: Record<string, string> | null) => void;
    showMapModalForPlannedTrail?: (id: string) => void;
    showMapModalForTrail?: (id: string) => void;
    showEditRecordingModal?: (id: string) => void;
    map?: mapboxgl.Map; // Assuming map instance might be on window
  }
}
</script>

<style scoped>
/* Cluster Navigation Bar */
.cluster-nav {
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(23, 24, 29, 0.9);
  border: 1px solid rgba(172, 176, 191, 0.3);
  border-radius: 20px;
  padding: 4px 12px;
  z-index: 10;
  white-space: nowrap;
}

.cluster-nav-btn {
  background: none;
  border: none;
  color: #ccc;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.cluster-nav-btn:hover {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.1);
}

.cluster-nav-btn .material-icons {
  font-size: 20px;
}

.cluster-nav-label {
  color: #fff;
  font-size: 0.85rem;
  font-weight: 500;
  user-select: none;
}

/* Main trail card container - Ensure position: relative on PARENT */
.trail-data-card {
  /* --- CORE POSITIONING RULES --- */
  /* These position the card relative to its nearest positioned ancestor */
  position: absolute;   /* Relative to nearest positioned ancestor */
  bottom: 1dvh;        /* Adjusted bottom position */
  left: 0;            /* Align left edge to ancestor's left */
  right: 0;           /* Align right edge to ancestor's right */
  margin: 1em auto;   /* Centers horizontally when left/right=0 */
  width: 80%;         /* Percentage width of the ancestor */
  max-width: 960px;   /* Max pixel width */
  z-index: 1000;      /* Stacking order */
  /* --- END CORE POSITIONING --- */

  display: flex;
  flex-direction: row;
  border-radius: 10px;
  background: rgba(23, 24, 29, 0.85);
  border: 1px solid rgba(172, 176, 191, 0.3); /* Softer border */
  color: #ffffff; /* Ensure text is visible */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3); /* Add shadow */
}

.trail-data-card.reduce {
  max-width: 510px;
}

/* == Top Right Controls Styling == */
.card-controls {
  position: absolute; /* Position this container */
  top: 8px;
  right: 8px;
  z-index: 10; /* Above other content */
  display: flex; /* Arrange buttons horizontally */
  align-items: center; /* Align items vertically */
  gap: 4px; /* Add space between the buttons */
}

/* Style for buttons INSIDE the container */
.card-controls .close-button,
.card-controls .hide-show {
  /* position: absolute; REMOVED - positioned by flex container */
  font-size: 22px; /* Consistent size */
  cursor: pointer;
  padding: 5px; /* Click area */
  color: #ccc;
  transition: color 0.2s ease, background-color 0.2s ease;
  border-radius: 50%; /* Optional: circle background */
  background-color: rgba(0, 0, 0, 0.1); /* Optional: slight bg */
  line-height: 1; /* Prevent extra space */
  display: flex; /* Ensure icon centers within padding */
  align-items: center;
  justify-content: center;
}

.card-controls .close-button:hover,
.card-controls .hide-show:hover {
  color: #fff;
  background-color: rgba(0, 0, 0, 0.4); /* Darker bg on hover */
}

/* Adjust positioning when reduced (container is already top-right) */
.trail-data-card.reduce .card-controls {
  /* Usually okay, but adjust `right` if layout shifts significantly */
  /* right: 15px; */ /* Example if padding/margins change */
}


/* Dropdown Menu */
.dropdown-menu{
  overflow: hidden;
  background-color: rgba(40, 42, 50, 0.95);
  border: 1px solid rgba(172, 176, 191, 0.3);
  padding: 0.25rem 0;
  min-width: auto; /* Allow dropdown to be smaller */
}

.dropdown-item{
  cursor: pointer;
  color: #ccc;
  padding: 0.35rem 1rem;
  font-size: 0.8rem;
  transition: background-color 0.2s ease, color 0.2s ease;
  white-space: nowrap; /* Prevent wrapping */
}
.dropdown-item:hover, .dropdown-item:focus {
  background-color: rgba(70, 73, 85, 0.9);
  color: #fff;
}
.dropdown-item a {
  color: inherit;
  text-decoration: none;
  display: block;
}


/* Left Panel */
.left-panel {
  flex: 0 0 50%;
  border-right: 1px solid rgba(172, 176, 191, 0.3);
  display: flex;
  flex-direction: row;
  align-items: stretch;
  min-height: 110px; /* Minimum height */
}

.trail-data-card.reduce .left-panel {
  flex: 1; /* Take full width when reduced */
  border-right: none; /* No border when reduced */
}

/* Right Panel: Elevation Profile */
.right-panel {
  flex: 1; /* Allow right panel to grow */
  padding: 10px 15px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.trail-data-card.reduce .right-panel {
  display: none;
}

/* Thumbnail container */
.thumbnail-container {
  width: 110px;
  flex-shrink: 0;
  height: 100%; /* Fill height of left panel */
  overflow: hidden;
  display: flex;
  align-items: center; /* Added for vertical centering if image aspect ratio differs */
  justify-content: center; /* Added for horizontal centering */
  border-top-left-radius: 9px; /* Match parent border radius - 1px */
  border-bottom-left-radius: 9px;
}

.thumbnail-container img {
  width: 100%;
  height: 100%; /* Fill container */
  object-fit: cover; /* Cover maintains aspect ratio */
  display: block; /* Remove potential extra space */
}

.left-panel p, .left-panel a {
  margin-bottom: 0.25rem;
  line-height: 1.3;
}
.left-panel strong {
  color: #fff;
  font-size: 1.1em;
  display: block; /* Ensure proper spacing */
  margin-bottom: 0.1rem;
}
.left-panel a {
  color: #eee;
  text-decoration: none !important;
  transition: color 0.2s ease;
}
.left-panel a:hover {
  color: #fff;
  text-decoration: underline !important;
}
.text-muted {
  color: #aaa !important;
}

/* Badges */
.badge {
  background-color: rgba(60, 63, 73, 0.8);
  border: none;
  font-weight: 500;
  vertical-align: middle; /* Align badges inline */
  padding: 0.3em 0.6em !important; /* Adjust padding */
  font-size: 0.8em; /* Slightly smaller badges */
}
.badge .material-icons {
  font-size: 1.1em;
  vertical-align: text-bottom; /* Align icon better with text */
  margin-right: 2px; /* Space between icon and text */
}
/* Ensure badges wrap correctly */
.left-panel .d-flex.flex-row {
  flex-wrap: wrap;
  gap: 5px; /* Add gap between wrapped items */
}


/* Elevation chart container */
.elevation-profile-placeholder {
  width: 100%;
  height: 100%;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-container {
  width: 100%;
  height: 100%;
  max-height: 100px;
  position: relative;
}

.chart-container canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

/* Card transition */
.trail-card-slide-enter-active,
.trail-card-slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.4s ease;
}

.trail-card-slide-enter-from,
.trail-card-slide-leave-to {
  transform: translateY(110%);
  opacity: 0;
}

.trail-card-slide-leave-from,
.trail-card-slide-enter-to {
  transform: translateY(0);
  opacity: 1;
}


/* Responsive Adjustments */
@media screen and (max-width: 992px) {
  .trail-data-card {
    width: 80%; /* Wider on medium screens */
  }
  .left-panel {
    width: 50%;
    flex: 1 1 auto;
    border-right: none;
    border-bottom: 1px solid rgba(172, 176, 191, 0.2);
    border-bottom-left-radius: 0; /* Reset radius */
  }
  .right-panel {
    width: 50%;
    flex: 1 1 auto;
    min-height: 100px;
    padding: 10px;
    border-bottom-left-radius: 9px; /* Apply radius */
    border-bottom-right-radius: 9px;
  }
  .trail-data-card.reduce .left-panel {
    border-bottom: none; /* No bottom border when reduced */
    border-bottom-left-radius: 9px; /* Apply radius */
    border-bottom-right-radius: 9px;
  }
  .trail-data-card.reduce .right-panel {
    display: none; /* Keep hiding right panel when reduced */
  }
  .thumbnail-container {
    width: 100px; /* Adjust thumbnail */
    height: 100px; /* Fixed height might work better */
    border-bottom-left-radius: 0; /* Reset */
    border-top-right-radius: 0; /* Reset when stacked */
  }
}


@media screen and (max-width: 768px) {
  .trail-data-card {
    width: 70%;   /* Adjusted width */
    left: 2.5%;   /* Use left/right for centering */
    right: 2.5%;
    margin-left: auto; /* Reset margin auto */
    margin-right: auto;
    /* flex-direction: column; Inherited */
  }
  /* Keep column layout */
  .right-panel:not(.force-show) { /* Hide graph panel by default */
    display: none;
  }
  .left-panel {
    border-bottom: none; /* No border needed if right panel is hidden */
    border-bottom-left-radius: 9px; /* Apply radius */
    border-bottom-right-radius: 9px;
  }
  .thumbnail-container {
    width: 80px;
    height: 80px;
  }
  .left-panel .px-2 {
    padding: 8px 12px !important;
  }
  .left-panel strong {
    font-size: 1em;
  }
  .left-panel p {
    font-size: 0.9em;
  }
  .badge {
    font-size: 0.75em;
    padding: 0.25rem 0.4rem !important;
  }
  .badge .material-icons {
    font-size: 1em;
  }
  /* Adjust controls position slightly */
  .card-controls {
    top: 5px;
    right: 5px;
  }
  .card-controls .close-button,
  .card-controls .hide-show {
    font-size: 20px;
    padding: 4px;
  }

  /* Hide all tooltips on mobile screens */
  :global(.tooltip) {
    display: none !important;
  }
}

@media screen and (max-width: 480px) {
  .trail-data-card {
    width: 95%;
  }
  .left-panel {
    flex-direction: column; /* Stack thumbnail on top of text */
  }
  .thumbnail-container {
    display: none !important;
  }
  .left-panel .px-2 {
    padding: 12px !important;
  }
  .badge {
    font-size: 0.7em;
  }
  .hide-show{
    display: none !important;
  }
}

/* Tooltip Custom Class */
:global(.tooltip.weather-tooltip .tooltip-inner) {
  background-color: rgba(255, 255, 255, 0.95);
  color: #333;
  font-size: 0.85rem;
  padding: 6px 10px;
  border-radius: 4px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.2);
}
:global(.tooltip.weather-tooltip .tooltip-arrow::before) {
  border-top-color: rgba(255, 255, 255, 0.95);
}

/* Bootstrap 5 Tooltip Arrow Color Overrides */
:global(.bs-tooltip-auto[data-popper-placement^=top] .tooltip-arrow::before),
:global(.bs-tooltip-top .tooltip-arrow::before){
  border-top-color: rgba(255, 255, 255, 0.95) !important;
}
:global(.bs-tooltip-auto[data-popper-placement^=bottom] .tooltip-arrow::before),
:global(.bs-tooltip-bottom .tooltip-arrow::before){
  border-bottom-color: rgba(255, 255, 255, 0.95) !important;
}
:global(.bs-tooltip-auto[data-popper-placement^=left] .tooltip-arrow::before),
:global(.bs-tooltip-left .tooltip-arrow::before){
  border-left-color: rgba(255, 255, 255, 0.95) !important;
}
:global(.bs-tooltip-auto[data-popper-placement^=right] .tooltip-arrow::before),
:global(.bs-tooltip-right .tooltip-arrow::before){
  border-right-color: rgba(255, 255, 255, 0.95) !important;
}

/* Elevation Marker Styling */
:global(.elevation-marker) { /* Use :global for unscoped CSS */
  display: block;
  border: 2px solid #fff;
  background-color: #e74c3c; /* Example color: reddish */
  border-radius: 50%;
  width: 12px; /* Marker size */
  height: 12px;
  box-shadow: 0 0 5px rgba(0,0,0,0.5);
  cursor: pointer;
  /* Ensure anchor is correct if using custom element */
  transform: translate(-50%, -100%); /* Centers horizontally, anchors bottom */
}
</style>
